// 1. React
import React, { useState, useEffect, useCallback } from 'react';

// 2. Third-party
import { Table, Button, Tag, Space, Popconfirm, message, Modal, Input, Empty, Collapse, Spin } from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';

// 3. Services
import { propertyService, globalFormService } from '@iso/services';
import { getFormPurposeLabel } from '@iso/constants/formPurposes';

// 4. Reused builder + preview from the Library page
import LibraryFormBuilderDrawer from '@iso/containers/Pages/AdminPages/ManageLibraryForms/LibraryFormBuilderDrawer';
import PreviewFormModal from '@iso/containers/Pages/AdminPages/ManageLibraryForms/PreviewFormModal';

// 5. The Forms modal the Topbar opens — adding a form happens in one place, so
// "Add Form" here opens the same thing rather than a second, divergent flow.
// Relative path: src/Layout has no @iso alias.
import FormsModal from '../../../../Layout/Topbar/FormModal';

const { Panel } = Collapse;

const SUCCESS_CODE = '0000';

const UNCATEGORISED = 'Uncategorised';

// DEPRECATED — "Add from Library" (copy-on-adopt) is superseded by the single Add
// Form flow on /manage-forms, which is now the only way a form is added to a
// property. The picker, its modal and propertyService.adoptCheckInForm are kept
// intact (the endpoint still answers, and answers consistently) but hidden behind
// this flag; flip it to true to bring the entry point back.
const ADOPT_FROM_LIBRARY_ENABLED = false;

const hasSchema = (form) => !!(form && form.schema_json && Array.isArray(form.schema_json.steps) && form.schema_json.steps.length);

const isContractorPurpose = (form) => form && form.purpose === 'contractor_check_in';

// Check-In & Attendance forms carry is_checkin_form; their Primary is scoped per
// Form Purpose (Visitor + Contractor coexist). Every other category has one Primary.
const isCheckInForm = (form) => !!(form && form.is_checkin_form);

const categoryNameOf = (form) => (form && form.category_global_form_data && form.category_global_form_data.name) || UNCATEGORISED;

// Template picker: browse published Library forms and adopt one for this property.
const AdoptFromLibraryModal = ({ visible, onCancel, onAdopt }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [adoptingId, setAdoptingId] = useState(null);

  const fetchList = useCallback(() => {
    setLoading(true);
    // Published templates via the non-admin-gated endpoint (property owners/admins
    // are not system admins, so the admin-only list() would 'Permission denied').
    // Filter client-side by the search term.
    globalFormService
      .getLivedForms()
      .then((res) => {
        if (res && res.code === SUCCESS_CODE) {
          const all = res.global_forms || [];
          const term = (search || '').trim().toLowerCase();
          setItems(term ? all.filter((f) => (f.name || '').toLowerCase().includes(term)) : all);
        }
      })
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => {
    if (visible) {
      fetchList();
    }
  }, [visible, fetchList]);

  const columns = [
    { title: 'Template', dataIndex: 'name', key: 'name' },
    { title: 'Type', dataIndex: 'type', key: 'type', width: 90 },
    {
      title: '',
      key: 'adopt',
      width: 120,
      render: (_, form) => (
        <Button
          size="small"
          type="primary"
          className="btn-success"
          loading={adoptingId === form.id}
          onClick={() => {
            setAdoptingId(form.id);
            Promise.resolve(onAdopt(form.id)).finally(() => setAdoptingId(null));
          }}
        >
          Adopt
        </Button>
      ),
    },
  ];

  return (
    <Modal title="Add from Library" visible={visible} onCancel={onCancel} footer={null} width={720}>
      <Input.Search
        placeholder="Search templates"
        allowClear
        onSearch={(value) => setSearch(value)}
        onChange={(e) => { if (!e.target.value) { setSearch(''); } }}
        style={{ marginBottom: 12 }}
      />
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={items}
        columns={columns}
        pagination={{ pageSize: 8 }}
      />
    </Modal>
  );
};

/**
 * Per-property "Check-In Forms" manager (Phase 6 + Phase 8). Lists the property's
 * forms grouped by Form Category, lets an owner/admin adopt a Library template
 * (copy-on-adopt), customize it in the shared builder, publish, and set a Primary.
 *
 * Primary scoping mirrors the backend (FormService / actionSetPrimaryCheckInForm):
 *   • Check-In & Attendance — one Primary per Form Purpose (Visitor Primary AND
 *     Contractor Primary can coexist; these drive the public QR landing).
 *   • Every other category — exactly one Primary per category.
 */
const CheckInFormsManagement = ({ propertyId }) => {
  const [formsData, setFormsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [builder, setBuilder] = useState({ visible: false, form: null });
  const [preview, setPreview] = useState({ visible: false, form: null });
  const [pickerVisible, setPickerVisible] = useState(false);
  const [formsModalVisible, setFormsModalVisible] = useState(false);

  const load = useCallback(() => {
    if (!propertyId) {
      return;
    }
    setLoading(true);
    // Dedicated endpoint, not viewProperty(): this tab needs the form list and the
    // two Primary pointers, and viewProperty serializes the whole property — users,
    // their images and managers, every primary object with its counts, metas, hosts
    // — to deliver them. It re-runs after every action below, so the difference is
    // paid on each one.
    propertyService
      .getCheckInForms(propertyId)
      .then((res) => {
        if (res && res.code === SUCCESS_CODE) {
          setFormsData(res);
          return;
        }
        // A refusal arrives as HTTP 200 with a non-success code, so it lands here
        // rather than in the catch. Without this it fell through silently and the
        // render below — which only knows `loaded` and an empty list — told the
        // owner their property has no forms. Saying nothing is the worst of the
        // three answers when the truth is "we could not read them".
        message.error((res && res.message) || 'Failed to load check-in forms');
      })
      .catch(() => message.error('Failed to load check-in forms'))
      .finally(() => {
        setLoading(false);
        setLoaded(true);
      });
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const forms = (formsData && formsData.forms) || [];
  const primaryId = formsData && formsData.primary_check_in_form_id;
  const contractorId = formsData && formsData.contractor_sign_in_form_id;

  const runAction = (promise, successMsg) => promise.then((res) => {
    if (res && res.code === SUCCESS_CODE) {
      message.success(successMsg);
      load();
    } else {
      message.error((res && res.message) || 'Action failed');
    }
    return res;
  });

  const doSetPrimary = (form) => {
    let msg;
    if (isCheckInForm(form)) {
      msg = isContractorPurpose(form)
        ? 'Contractor primary check-in form updated'
        : 'Visitor primary check-in form updated';
    } else {
      msg = `${categoryNameOf(form)} primary updated`;
    }
    return runAction(propertyService.setPrimaryCheckInForm(propertyId, form.id), msg);
  };
  const doTogglePublish = (form) => runAction(propertyService.updateCheckInForm(propertyId, form.id, { is_draft: form.is_draft ? 0 : 1 }), form.is_draft ? 'Form published' : 'Form unpublished');
  // Deleting a form that has been filled in takes real records out of use, so it
  // needs a second, informed confirmation rather than a generic "are you sure".
  // First attempt goes in unforced: the API refuses and reports the counts, and only
  // then do we name what is at stake and ask again.
  const describeSubmissions = (counts, total) => {
    const parts = [];
    if (counts?.schema) { parts.push(`${counts.schema} form ${counts.schema === 1 ? 'submission' : 'submissions'}`); }
    if (counts?.visitor) { parts.push(`${counts.visitor} visitor check-${counts.visitor === 1 ? 'in' : 'ins'}`); }
    if (counts?.user) { parts.push(`${counts.user} completed by ${counts.user === 1 ? 'a worker' : 'workers'}`); }
    return parts.length ? parts.join(', ') : `${total} submission${total === 1 ? '' : 's'}`;
  };

  const confirmForceDelete = (form, res) => {
    const total = res.submission_total || 0;
    Modal.confirm({
      title: 'This form already has submissions',
      okText: 'Delete anyway',
      okButtonProps: { danger: true },
      cancelText: 'Keep form',
      width: 520,
      content: (
        <div>
          <p>
            <strong>{form.name}</strong> has <strong>{total}</strong> submission{total === 1 ? '' : 's'}
            {' '}({describeSubmissions(res.submission_counts, total)}).
          </p>
          <p>
            Deleting it removes the form from this property, its category list and its
            public QR link. <strong>The submissions themselves are kept</strong> and stay
            available for reporting — the form is retired, not erased.
          </p>
          <p style={{ marginBottom: 0 }}>
            If you only want to stop people filling it in, <strong>Unpublish</strong> it
            instead and it stays on the list.
          </p>
        </div>
      ),
      onOk: () => runAction(propertyService.deleteCheckInForm(propertyId, form.id, true), 'Form deleted'),
    });
  };

  const doDelete = (form) => propertyService.deleteCheckInForm(propertyId, form.id).then((res) => {
    if (res && res.code === SUCCESS_CODE) {
      message.success('Form deleted');
      load();
    } else if (res && res.requires_force) {
      confirmForceDelete(form, res);
    } else {
      message.error((res && res.message) || 'Action failed');
    }
    return res;
  });

  const saveFnFor = (form) => (data) => propertyService.updateCheckInForm(propertyId, form.id, data);

  const onAdopt = (globalFormId) => propertyService.adoptCheckInForm(propertyId, globalFormId).then((res) => {
    if (res && res.code === SUCCESS_CODE) {
      message.success('Template adopted — customize and publish it');
      setPickerVisible(false);
      load();
      if (res.form) {
        setBuilder({ visible: true, form: res.form });
      }
    } else {
      message.error((res && res.message) || 'Could not adopt template');
    }
    return res;
  });

  // Is this form currently the Primary of its scope?
  const isFormPrimary = (form) => {
    if (isCheckInForm(form)) {
      return isContractorPurpose(form)
        ? form.id === contractorId || !!form.is_primary
        : form.id === primaryId;
    }
    return !!form.is_primary;
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, form) => {
        const isVisitorPrimary = isCheckInForm(form) && form.id === primaryId && !isContractorPurpose(form);
        const isContractorPrimary = isCheckInForm(form)
          && (form.id === contractorId || (isContractorPurpose(form) && !!form.is_primary));
        const isCategoryPrimary = !isCheckInForm(form) && !!form.is_primary;
        return (
          <Space wrap>
            <span>{name}</span>
            {isVisitorPrimary && <Tag color="green">Visitor Primary</Tag>}
            {isContractorPrimary && <Tag color="blue">Contractor Primary</Tag>}
            {isCategoryPrimary && <Tag color="green">Primary</Tag>}
            {form.purpose && <Tag>{getFormPurposeLabel(form.purpose) || form.purpose}</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Engine',
      key: 'engine',
      width: 110,
      render: (_, form) => (hasSchema(form) ? <Tag color="geekblue">Schema</Tag> : <Tag>Legacy</Tag>),
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, form) => (hasSchema(form) ? (form.is_draft ? <Tag color="orange">Draft</Tag> : <Tag color="green">Live</Tag>) : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, form) => {
        const isThisPrimary = isFormPrimary(form);
        // Mirror the backend: only categorised or check-in forms have a scope to
        // become Primary of; a draft cannot be the public/runtime target.
        const canSetPrimary = (isCheckInForm(form) || !!form.category_global_form_id)
          && !isThisPrimary && !form.is_draft;
        const primaryConfirm = isCheckInForm(form)
          ? (isContractorPurpose(form)
            ? 'Make this the Contractor Primary check-in form for the property?'
            : 'Make this the Visitor Primary check-in form for the property?')
          : `Make this the Primary ${categoryNameOf(form)} form for the property?`;
        // A Primary form (of any scope) must not be deletable while it holds the slot.
        const canDelete = hasSchema(form) && !isThisPrimary;

        return (
          <Space wrap>
            {hasSchema(form) && (
              <Button size="small" icon={<EyeOutlined />} onClick={() => setPreview({ visible: true, form })}>Preview</Button>
            )}
            {hasSchema(form) && (
              <Button size="small" icon={<EditOutlined />} onClick={() => setBuilder({ visible: true, form })}>Edit</Button>
            )}
            {hasSchema(form) && (
              <Button size="small" onClick={() => doTogglePublish(form)}>{form.is_draft ? 'Publish' : 'Unpublish'}</Button>
            )}
            {canSetPrimary && (
              <Popconfirm title={primaryConfirm} okText="Yes" cancelText="No" onConfirm={() => doSetPrimary(form)}>
                <Button size="small" type="primary" className="btn-success">Set Primary</Button>
              </Popconfirm>
            )}
            {canDelete && (
              <Popconfirm
                title="Delete this check-in form? Its submissions are kept."
                okText="Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
                onConfirm={() => doDelete(form)}
              >
                <Button size="small" danger icon={<DeleteOutlined />}>Delete</Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  // Group forms by Form Category. Check-In & Attendance sorts first (it drives the
  // public QR); the rest keep insertion order.
  const groups = [];
  const indexByName = {};
  forms.forEach((form) => {
    const name = categoryNameOf(form);
    if (indexByName[name] === undefined) {
      indexByName[name] = groups.length;
      groups.push({ name, isCheckIn: isCheckInForm(form), forms: [] });
    }
    const group = groups[indexByName[name]];
    group.forms.push(form);
    if (isCheckInForm(form)) {
      group.isCheckIn = true;
    }
  });
  groups.sort((a, b) => (b.isCheckIn ? 1 : 0) - (a.isCheckIn ? 1 : 0));
  const activeKeys = groups.map((g) => g.name);

  return (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <span style={{ color: '#666' }}>
          Forms are grouped by Form Category. Each category has one Primary — except
          Check-In &amp; Attendance, which keeps a Visitor Primary and a Contractor
          Primary (set the Form Purpose, publish, then Set Primary).
        </span>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setFormsModalVisible(true)}>Add Form</Button>
          {ADOPT_FROM_LIBRARY_ENABLED && (
            <Button icon={<PlusOutlined />} onClick={() => setPickerVisible(true)}>Add from Library</Button>
          )}
        </Space>
      </div>

      {/*
        `groups` is derived from `formsData`, which is null until the request lands —
        so keying the empty state off group count alone told the user "No forms yet"
        for the whole fetch, then swapped in their forms. Wait for the first response
        before making that claim.
      */}
      {!loaded ? (
        <div style={{ padding: '48px 0', textAlign: 'center' }}>
          <Spin tip="Loading forms…" />
        </div>
      ) : groups.length === 0 ? (
        <Empty description="No forms yet.">
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setFormsModalVisible(true)}>Add Form</Button>
        </Empty>
      ) : (
        <Collapse defaultActiveKey={activeKeys}>
          {groups.map((group) => (
            <Panel
              key={group.name}
              header={(
                <Space>
                  <strong>{group.name}</strong>
                  {/* {group.isCheckIn && <Tag color="geekblue">Primary per Purpose</Tag>} */}
                  <span style={{ color: '#999' }}>{group.forms.length} form{group.forms.length === 1 ? '' : 's'}</span>
                </Space>
              )}
            >
              <Table
                rowKey="id"
                loading={loading}
                dataSource={group.forms}
                columns={columns}
                pagination={false}
              />
            </Panel>
          ))}
        </Collapse>
      )}

      {builder.visible && (
        <LibraryFormBuilderDrawer
          visible={builder.visible}
          form={builder.form}
          mode="property"
          propertyId={propertyId}
          saveFn={saveFnFor(builder.form)}
          onClose={() => setBuilder({ visible: false, form: null })}
          onSaved={load}
        />
      )}

      <PreviewFormModal
        visible={preview.visible}
        form={preview.form}
        onCancel={() => setPreview({ visible: false, form: null })}
      />

      {/*
        Mounted only while open — it renders the whole Forms page inside, and a
        permanently mounted copy would duplicate that page's fetches (the same
        reason the Topbar mounts it conditionally). Reload on close so a form
        added in there shows up in these groups straight away.
      */}
      {formsModalVisible && (
        <FormsModal
          isShowFormModal={formsModalVisible}
          cancelFormModal={() => {
            setFormsModalVisible(false);
            load();
          }}
        />
      )}

      {ADOPT_FROM_LIBRARY_ENABLED && (
        <AdoptFromLibraryModal
          visible={pickerVisible}
          onCancel={() => setPickerVisible(false)}
          onAdopt={onAdopt}
        />
      )}
    </div>
  );
};

export default CheckInFormsManagement;
